<template>
  <div class="forum_profile">
    <!-- 用户信息区：头像 + 昵称 + 编辑 -->
    <div class="forum_profile__user">
      <div class="forum_profile__avatar">
        <img :src="me.avatar || FORUM_DEFAULT_AVATAR" alt="" />
      </div>
      <div class="forum_profile__info">
        <div class="forum_profile__name_row">
          <span class="text_ellipsis">{{ me.nickname || '昵称为空' }}</span>
          <span class="forum_profile__edit" @click="goEditProfile">编辑</span>
        </div>
        <div v-if="boundAccountName" class="forum_profile__bind_account text_ellipsis">已绑定账号：{{ boundAccountName }}</div>
      </div>
    </div>

    <!-- 数据统计卡片：帖子 / 点赞 / 收藏 -->
    <div class="forum_profile__stats">
      <div class="forum_profile__stat_item" @click="switchTab('mine')">
        <div class="forum_profile__stat_num">{{ formatCount(me.postsCount) }}</div>
        <div class="forum_profile__stat_label">我的帖子</div>
      </div>
      <div class="forum_profile__stat_item" @click="switchTab('praise')">
        <div class="forum_profile__stat_num">{{ formatCount(me.praiseCount) }}</div>
        <div class="forum_profile__stat_label">我的点赞</div>
      </div>
      <div class="forum_profile__stat_item" @click="switchTab('collect')">
        <div class="forum_profile__stat_num">{{ formatCount(me.collectCount) }}</div>
        <div class="forum_profile__stat_label">我的收藏</div>
      </div>
    </div>

    <!-- Tab 行：我的帖子 / 我的点赞 / 我的收藏 -->
    <div class="forum_profile__tabs">
      <div
        v-for="item in tabItems"
        :key="item.key"
        class="forum_profile__tab"
        :class="{ 'forum_profile__tab--active': tab === item.key }"
        @click="switchTab(item.key)"
      >
        {{ item.label }}
        <span v-if="tab === item.key" class="forum_profile__tab_line" />
      </div>
    </div>

    <!-- 列表区：按 tab 分别渲染 -->
    <div class="forum_profile__list">
      <van-list
        v-show="tab === 'mine'"
        v-model="lists.mine.loading"
        :finished="lists.mine.finished"
        :immediate-check="false"
        :offset="80"
        :finished-text="mineFinishedText"
        @load="onLoadMore"
      >
        <van-swipe-cell v-for="post in lists.mine.list" :key="post.postsId">
          <PostItem :data="post" @click="onPostClick" @praise="onPraise" @collect="onCollect" />
          <template #right>
            <van-button
              square
              type="danger"
              class="forum_profile__delete_btn"
              @click="onDeleteMine(post)"
            >删除</van-button>
          </template>
        </van-swipe-cell>
        <EmptyState
          v-if="!lists.mine.loading && lists.mine.list.length === 0 && lists.mine.finished"
          text="还没发过帖"
        />
      </van-list>

      <van-list
        v-show="tab === 'praise'"
        v-model="lists.praise.loading"
        :finished="lists.praise.finished"
        :immediate-check="false"
        :offset="80"
        :finished-text="praiseFinishedText"
        @load="onLoadMore"
      >
        <PostItem
          v-for="post in lists.praise.list"
          :key="post.postsId"
          :data="post"
          @click="onPostClick"
          @praise="onPraise"
          @collect="onCollect"
        />
        <EmptyState
          v-if="!lists.praise.loading && lists.praise.list.length === 0 && lists.praise.finished"
          text="还没点赞过帖子"
        />
      </van-list>

      <van-list
        v-show="tab === 'collect'"
        v-model="lists.collect.loading"
        :finished="lists.collect.finished"
        :immediate-check="false"
        :offset="80"
        :finished-text="collectFinishedText"
        @load="onLoadMore"
      >
        <PostItem
          v-for="post in lists.collect.list"
          :key="post.postsId"
          :data="post"
          @click="onPostClick"
          @praise="onPraise"
          @collect="onCollect"
        />
        <EmptyState
          v-if="!lists.collect.loading && lists.collect.list.length === 0 && lists.collect.finished"
          text="还没收藏过帖子"
        />
      </van-list>
    </div>
  </div>
</template>

<script>
import { Button, Dialog, List, SwipeCell, Toast } from 'vant';
import { mapState } from 'vuex';
import {
  collectOrCancelPosts,
  deleteOwnPost,
  getUserCenter,
  praiseOrCancelPosts,
} from '@/api/forum';
import { createForumPostList } from '@/utils/forum/postList';
import { formatCount } from '@/utils/forum/format';
import { isInteractionOk, toastInteraction } from '@/utils/forum/forumInteraction';
import EmptyState from './components/EmptyState.vue';
import PostItem from './components/PostItem.vue';
import { FORUM_DEFAULT_AVATAR } from './constants';
import trackingMixin, { EVENT_TYPE_ENUM } from './mixins/tracking';
import './forum-common.css';

export default {
  name: 'ForumProfile',
  mixins: [trackingMixin],
  components: {
    [List.name]: List,
    [SwipeCell.name]: SwipeCell,
    [Button.name]: Button,
    EmptyState,
    PostItem,
  },
  data() {
    return {
      FORUM_DEFAULT_AVATAR,
      me: {},
      tab: 'mine',
      tabItems: [
        { key: 'mine', label: '我的帖子' },
        { key: 'praise', label: '我的点赞' },
        { key: 'collect', label: '我的收藏' },
      ],
      skipInitialActivated: false,
      lists: {
        mine: createForumPostList('mine'),
        collect: createForumPostList('collect'),
        praise: createForumPostList('praise'),
      },
    };
  },
  computed: {
    ...mapState('account', {
      accountUserInfo: (state) => state.userInfo || {},
    }),
    current() {
      return this.lists[this.tab];
    },
    mineFinishedText() {
      return this.getFinishedText(this.getListItems('mine'));
    },
    praiseFinishedText() {
      return this.getFinishedText(this.getListItems('praise'));
    },
    collectFinishedText() {
      return this.getFinishedText(this.getListItems('collect'));
    },
    boundAccountName() {
      const info = this.accountUserInfo || {};
      return info.game_account || '';
    },
  },
  activated() {
    if (this.skipInitialActivated) {
      this.skipInitialActivated = false;
      return;
    }
    this.bootstrap();
    this.trackPageShow();
  },
  created() {
    this.skipInitialActivated = true;
    this.bootstrap();
    this.trackPageShow();
  },
  methods: {
    formatCount,
    getTabLabel(key) {
      const item = this.tabItems.find((tab) => tab.key === key);
      return (item && item.label) || '';
    },
    getListItems(key) {
      const lists = this.lists || {};
      const current = lists[key] || {};
      return current.list;
    },
    getFinishedText(list) {
      return Array.isArray(list) && list.length > 0 ? '没有更多了' : '';
    },
    trackPageShow() {
      this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_square_page_show, {
        page_type: '个人主页',
      });
    },
    trackTabClick(tab) {
      this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_user_center_tab_click, {
        page_type: '个人主页',
        tab_type: this.getTabLabel(tab),
      });
    },
    async bootstrap() {
      await this.fetchMe();
      await this.current.load(true);
    },
    async fetchMe() {
      const res = await getUserCenter();
      if (res && res.data) this.me = res.data;
    },
    async switchTab(nextTab) {
      this.trackTabClick(nextTab);
      this.tab = nextTab;
      // 每次切换/点击 tab 都重新请求首屏接口。
      await this.current.load(true);
    },
    onLoadMore() {
      this.current.load(false);
    },
    goEditProfile() {
      this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_square_button, {
        page_type: '个人主页',
        button_type: '头像昵称编辑',
      });
      this.$router.push({ name: 'forum-edit-profile' });
    },
    onPostClick(post) {
      this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_user_center_post_click, {
        page_type: '个人主页',
        tab_type: this.getTabLabel(this.tab),
        post_id: post.postsId || post.postId || '',
        post_title: post.title || '',
      });
    },
    trackPostOpt(post, buttonType) {
      this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_home_post_opt_click, {
        page_type: '个人主页',
        tab_type: this.getTabLabel(this.tab),
        post_id: post.postsId || post.postId || '',
        post_title: post.title || '',
        button_type: buttonType,
      });
    },
    refreshStatsLater() {
      this.fetchMe();
    },
    async onPraise(post) {
      try {
        this.trackPostOpt(post, post.praise ? '取消点赞' : '点赞');
        const res = await praiseOrCancelPosts(post.postsId, post.praise ? 2 : 1);
        const ok = toastInteraction(res, {
          successMsg: post.praise ? '已取消点赞' : '已点赞',
        });
        if (!ok) return;
        post.praise = !post.praise;
        post.praiseNum = (post.praiseNum || 0) + (post.praise ? 1 : -1);
        this.refreshStatsLater();
      } catch {
        // ignore
      }
    },
    async onCollect(post) {
      try {
        this.trackPostOpt(post, post.collect ? '取消收藏' : '收藏');
        const res = await collectOrCancelPosts(post.postsId, post.collect ? 2 : 1);
        if (!isInteractionOk(res)) {
          toastInteraction(res, { fallbackMsg: '操作失败' });
          return;
        }
        post.collect = !post.collect;
        post.collectNum = (post.collectNum || 0) + (post.collect ? 1 : -1);
        this.refreshStatsLater();
      } catch {
        // ignore
      }
    },
    async onDeleteMine(post) {
      try {
        await Dialog.confirm({ title: '提示', message: '确定删除该帖子？' });
        await deleteOwnPost(post.postsId);
        Toast.success('已删除');
        this.lists.mine.list = this.lists.mine.list.filter((item) => item.postsId !== post.postsId);
        this.refreshStatsLater();
      } catch {
        // 用户取消或删除失败均忽略
      }
    },
  },
};
</script>

<style scoped>
.forum_profile {
  min-height: 100vh;
  padding-bottom: 24px;
  background: #fff;
}

.forum_profile__user {
  display: flex;
  align-items: center;
  padding: 18px 40px;
  background: #fff;
}

.forum_profile__avatar {
  width: 68px;
  height: 68px;
  border-radius: 50%;
  overflow: hidden;
  background: #e5e7eb;
  flex-shrink: 0;
}

.forum_profile__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.forum_profile__avatar_placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 20px;
}

.forum_profile__info {
  flex: 1;
  min-width: 0;
  margin-left: 12px;
}

.forum_profile__name_row {
  display: flex;
  align-items: center;
}

.forum_profile__name_row span:first-child {
  color: #111827;
  font-size: 14px;
  font-weight: 500;
}

.forum_profile__edit {
  flex-shrink: 0;
  margin-left: 8px;
  color: #0249ff;
  font-size: 12px;
}

.forum_profile__bind_account {
  margin-top: 6px;
  color: #6b7280;
  font-size: 12px;
  line-height: 1.2;
}

.forum_profile__stats {
  display: flex;
  align-items: center;
  margin: 6px 18px;
  padding: 12px 58px;
  border-radius: 12px;
  background: #f3f3f3;
  text-align: center;
}

.forum_profile__stat_item {
  flex: 1;
}

.forum_profile__stat_num {
  margin-bottom: 8px;
  color: #111827;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
}

.forum_profile__stat_label {
  color: #6b7280;
  font-size: 12px;
  line-height: 1;
}

.forum_profile__tabs {
  display: flex;
  align-items: center;
  width: 100%;
  margin-top: 12px;
  padding: 0 16px;
  background: #fff;
  font-size: 14px;
}

.forum_profile__tab {
  position: relative;
  flex: 1;
  padding: 12px 0;
  color: #9ca3af;
  text-align: center;
}

.forum_profile__tab--active {
  color: #111827;
  font-weight: 500;
}

.forum_profile__tab_line {
  position: absolute;
  left: 50%;
  bottom: 4px;
  width: 24px;
  height: 3px;
  border-radius: 999px;
  background: #fd383c;
  transform: translateX(-50%);
}

.forum_profile__list {
  background: #fff;
}

.forum_profile__delete_btn {
  height: 100%;
}
</style>
